import { useQuery, useQueries } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions } from '@frontend/react-query-helpers';
import { SchemaBrandSettingsService } from '../service';
import { ListIO } from '../types';

export const getListQueryKey = (req?: ListIO['input']) => {
  const baseRequest = ['BrandSettings', 'List'] as const;
  if (req) {
    return [...baseRequest, req] as const;
  }
  return baseRequest;
};
type QueryKey = ReturnType<typeof getListQueryKey>;

/**
 * Returns a query for the `List` endpoint.
 *
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useListQuery = <E = unknown, D = ListIO['output']>(
  req: ListIO['input'],
  options?: LimitedSchemaQueryOptions<ListIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<ListIO['output'], E, D, QueryKey>({
    queryKey: getListQueryKey(req),
    queryFn: () => SchemaBrandSettingsService.List(req, httpOptions),
    ...options,
  });

/**
 * Returns an array of `List` queries.
 *
 * Each entry in `argsArr` contains a request plus optional query and HTTP options.
 */
export const useListQueries = <E = unknown, D = ListIO['output']>(
  argsArr: {
    req: ListIO['input'];
    options?: LimitedSchemaQueryOptions<ListIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getListQueryKey(req),
      queryFn: () => SchemaBrandSettingsService.List(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useListQuery<E, D>>[];
